import React, { KeyboardEvent, MouseEvent, SyntheticEvent, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { ProductImage } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import { Image } from '@noths/polaris-dev-ts-types';

import { sectionVisible } from 'src/redux/visibilityTracking';
import {
  PRODUCT_IMAGES_SECTION_LABEL_PLURAL,
  PRODUCT_IMAGES_TAB_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from '../constants';
import { useThumbnailFocusAndScroll } from '../hooks/useThumbnailFocusAndScroll';
import { PRODUCT_IMAGES_CAROUSEL_VISIBILITY_NAME } from '../tracking/events';
import {
  ImagesLocation,
  NextOrPreviousPayload,
  ProductImagesState,
  ThumbnailOrientation,
} from '../types';
import * as styles from '../styles/Thumbnails.styles';

interface ThumbnailsProps {
  images: Image[];
  currentImage: ProductImagesState['currentImage'];
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  onThumbnailClick: (imageIndex: number) => void;
  orientation: ThumbnailOrientation;
  idPrefix?: string;
  location: ImagesLocation;
}

export const Thumbnails = ({
  images,
  currentImage,
  onNextImage,
  onPrevImage,
  onThumbnailClick,
  orientation,
  idPrefix = '',
  location,
}: ThumbnailsProps) => {
  const dispatch = useDispatch();
  const { observedElementRef: productImagesCarouselRef } = useVisibilityObserver(() =>
    dispatch(sectionVisible({ sectionName: PRODUCT_IMAGES_CAROUSEL_VISIBILITY_NAME })),
  );

  const thumbnailRefs = useRef<HTMLAnchorElement[] | null[]>([]);
  const [thumbnailsHaveKeyboardFocus, setThumbnailsHaveKeyboardFocus] = useState(false);
  const prevImageKey = orientation === ThumbnailOrientation.HORIZONTAL ? 'ArrowLeft' : 'ArrowUp';
  const nextImageKey = orientation === ThumbnailOrientation.HORIZONTAL ? 'ArrowRight' : 'ArrowDown';
  const orientationStyles = styles[orientation];
  const visibilityTrackingEnabled = location !== 'Expanded image';

  useThumbnailFocusAndScroll({
    thumbnailsHaveKeyboardFocus,
    thumbnailRefs,
    currentImage,
    shouldScrollThumbnailIntoView: location !== 'Carousel',
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    switch (event.key) {
      case prevImageKey:
        onPrevImage({ location });
        event.preventDefault();
        break;
      case nextImageKey:
        onNextImage({ location });
        event.preventDefault();
        break;
    }
  };

  const handleThumbnailFocus = (event: SyntheticEvent<HTMLElement>) =>
    setThumbnailsHaveKeyboardFocus(isKeyboardTriggeredEvent(event));

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, imageIndex: number) => {
    event.preventDefault();
    onThumbnailClick(imageIndex);
  };

  return (
    <div
      ref={visibilityTrackingEnabled ? productImagesCarouselRef : null}
      css={[
        orientationStyles.thumbnails,
        thumbnailsHaveKeyboardFocus ? styles.thumbnailsWithKeyboardFocus : null,
      ]}
      role="tablist"
      aria-orientation={orientation}
      aria-label={PRODUCT_IMAGES_SECTION_LABEL_PLURAL}
      onFocus={handleThumbnailFocus}
      onBlur={() => setThumbnailsHaveKeyboardFocus(false)}
    >
      <ul css={orientationStyles.thumbnailList}>
        {images.map(({ href: src, title }, i) => (
          <li css={orientationStyles.thumbnailListItem} key={src}>
            <a
              href={getProductImageURL(src, 'original')}
              id={`${idPrefix}${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
              role="tab"
              aria-controls={`${idPrefix}${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
              aria-selected={i === currentImage}
              aria-label={`${title}, ${i + 1} of ${images.length}`}
              tabIndex={i === currentImage ? undefined : -1}
              css={styles.thumbnailImageLink}
              onKeyDown={handleKeyDown}
              onClick={(event) => handleClick(event, i)}
              ref={(elem) => (thumbnailRefs.current[i] = elem)}
            >
              <ProductImage
                src={src}
                size="mini"
                css={orientationStyles.thumbnailImage}
                alt={`${title}, thumbnail ${i + 1} of ${images.length}`}
              />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};
